/**
 * 사이즈 추천 로그인 플로우 localStorage 관리
 *
 * [플로우]
 * 1. 미로그인 상태에서 체형 입력 → '선택 완료' 클릭
 * 2. PENDING_SIZE_INPUT (체형 입력값), SHOULD_OPEN_SIZE_INFO_POPUP (바텀싯 노출 플래그) 저장
 * 3. 로그인 페이지로 이동 → 로그인 완료 → PDP 복귀
 * 4. SizeInfoContainer에서 조건에 따라 바텀싯 노출 또는 자동 저장
 *
 * [5분 유효기간]
 * - 사이즈 추천 → 로그인 → PDP 복귀 플로우 내에서만 유효하도록 제한
 * - 외부에서 로그인 후 접근 시 바텀싯 노출 방지
 */
export const SHOULD_OPEN_SIZE_INFO_POPUP = 'SHOULD_OPEN_SIZE_INFO_POPUP';
export const PENDING_SIZE_INPUT = 'PENDING_SIZE_INPUT';

const MAX_AGE = 5 * 60 * 1000;

export const savePendingSizeInput = (values: Record<string, any>) => {
  localStorage.setItem(PENDING_SIZE_INPUT, JSON.stringify({ values, timestamp: Date.now() }));
};

export const getPendingSizeInput = (): Record<string, any> | null => {
  const stored = localStorage.getItem(PENDING_SIZE_INPUT);
  if (!stored) return null;

  try {
    const { values, timestamp } = JSON.parse(stored);
    if (Date.now() - timestamp > MAX_AGE) {
      localStorage.removeItem(PENDING_SIZE_INPUT);
      return null;
    }
    return values;
  } catch {
    localStorage.removeItem(PENDING_SIZE_INPUT);
    return null;
  }
};

export const removePendingSizeInput = () => {
  localStorage.removeItem(PENDING_SIZE_INPUT);
};

export const setShouldOpenPopup = () => {
  localStorage.setItem(SHOULD_OPEN_SIZE_INFO_POPUP, JSON.stringify({ timestamp: Date.now() }));
};

export const getShouldOpenPopup = (): boolean => {
  const stored = localStorage.getItem(SHOULD_OPEN_SIZE_INFO_POPUP);
  if (!stored) return false;

  try {
    const { timestamp } = JSON.parse(stored);
    if (Date.now() - timestamp > MAX_AGE) {
      localStorage.removeItem(SHOULD_OPEN_SIZE_INFO_POPUP);
      return false;
    }
    return true;
  } catch {
    localStorage.removeItem(SHOULD_OPEN_SIZE_INFO_POPUP);
    return false;
  }
};

export const removeShouldOpenPopup = () => {
  localStorage.removeItem(SHOULD_OPEN_SIZE_INFO_POPUP);
};

/**
 * 사이즈 비교하기 -> 로그인 -> 직접 입력 페이지 열기 플로우
 * 1회 한정 동작을 보장하기 위해 consume 패턴 사용
 */
export const SHOULD_OPEN_DIRECT_INPUT = 'SHOULD_OPEN_DIRECT_INPUT';

export interface DirectInputRedirectData {
  catalogProductId: string;
  sizeCategoryId: string;
}

export const setShouldOpenDirectInput = (data: DirectInputRedirectData) => {
  localStorage.setItem(SHOULD_OPEN_DIRECT_INPUT, JSON.stringify({ ...data, timestamp: Date.now() }));
};

/**
 * 플래그를 읽으면서 동시에 삭제 (atomic) - 1회 동작 보장
 */
export const consumeShouldOpenDirectInput = (): DirectInputRedirectData | null => {
  const stored = localStorage.getItem(SHOULD_OPEN_DIRECT_INPUT);
  if (!stored) return null;

  // 읽자마자 바로 삭제 (1회 보장)
  localStorage.removeItem(SHOULD_OPEN_DIRECT_INPUT);

  try {
    const { catalogProductId, sizeCategoryId, timestamp } = JSON.parse(stored);
    if (Date.now() - timestamp > MAX_AGE) {
      return null;
    }
    return { catalogProductId, sizeCategoryId };
  } catch {
    return null;
  }
};
